import * as Sentry from '@sentry/react'

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'
  const release = import.meta.env.VITE_RELEASE_NUMBER
  
  if (!dsn) {
    return
  }

  Sentry.init({
    dsn,
    environment,
    release: release ? `prosci-portal@${release}` : undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    // Release Health
    autoSessionTracking: true,
    sendClientReports: true,
    // Ignore errors from third-party scripts and browser extensions
    ignoreErrors: [
      // ===== BROWSER/FRAMEWORK QUIRKS =====
      /ResizeObserver/, // Browser implementation quirks
      /WebSocket connection timeout/, // Expected on poor networks

      // ===== ONETRUST COOKIE CONSENT =====
      /Cannot read properties of undefined \(reading 'Domain'\)/,
      /undefined is not an object \(evaluating '.*\.Domain'\)/,
      /undefined is not an object \(evaluating '.*Domain'\)/,

      // ===== NEW RELIC BROWSER AGENT =====
      /Cannot convert object to primitive value/,
      /No default value/, // Backlog encoding error (PROSCI-PORTAL-8E)
      /can't convert Proxy to string/, // Firefox compatibility issue (PROSCI-PORTAL-8E)

      // ===== DYNAMIC IMPORTS (handled by lazyWithRetry with auto-reload) =====
      /Failed to fetch dynamically imported module/,
      /Loading chunk .* failed/,
      /Loading CSS chunk .* failed/,

      // ===== BROWSER EXTENSIONS =====
      /chrome-extension/,
      /moz-extension/,
      /safari-extension/,
      /webkit-masked-url/, // Safari's hidden extension URLs (PROSCI-PORTAL-9X)
      /ClownfishModule/, // Clownfish voice changer extension (PROSCI-PORTAL-97)
      /SymBrowser/, // Symantec browser extension (PROSCI-PORTAL-AD)
      /MetaMask/, // MetaMask crypto wallet (PROSCI-PORTAL-9W)
      /runtime\.sendMessage/, // Browser extension messaging (PROSCI-PORTAL-A4)
      /Message did timeout/, // Safari extension timeout (PROSCI-PORTAL-9X)

      // ===== CHAMELEON (chmln.js) =====
      /chmln/i, // Chameleon script errors (PROSCI-PORTAL-9P/9N/A1/A2/A3)
      /e\.data\.account/,
      /data\.account\.get/,

      // ===== NETWORK/ABORT ERRORS =====
      /Load failed/, // Network interruption (PROSCI-PORTAL-9Z/AE/9V)
      /AbortError/, // User navigation (PROSCI-PORTAL-A8)
      /The operation was aborted/,
      /NetworkError/,
      /net::ERR_/,
      /text\/html.*is not a valid JavaScript MIME type/, // CDN/network error (PROSCI-PORTAL-8G)

      // ===== REACT DOM RECONCILIATION (caused by browser extensions modifying DOM) =====
      /removeChild.*not a child/, // PROSCI-PORTAL-9K/9H
      /insertBefore.*not a child/,
      /Maximum call stack size exceeded/, // Extension infinite loops (PROSCI-PORTAL-99)

      // ===== VIEW TRANSITIONS =====
      /view transition aborted/i, // User rapid navigation (PROSCI-PORTAL-A0)

      // ===== NON-ERROR REJECTIONS =====
      /Non-Error promise rejection captured with value/, // Generic third-party (PROSCI-PORTAL-9J)
    ],
    // Ignore errors from these third-party script URLs
    denyUrls: [
      // ===== ONETRUST COOKIE CONSENT =====
      /otSDKStub\.js/,
      /onetrust/i,
      /cdn\.cookielaw\.org/,

      // ===== NEW RELIC BROWSER AGENT =====
      /newrelic/i,
      /browser-agent/,
      /nr-data\.net/,
      /bam\.nr-data\.net/,

      // ===== BROWSER EXTENSIONS =====
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-extension:\/\//,
      /^safari-web-extension:\/\//,
      /webkit-masked-url/, // Safari's hidden extension URLs

      // ===== CHAMELEON =====
      /chmln\.js/,
      /chameleon\.io/,
      /fast\.chameleon\.io/,
      /messo.*\.min\.js/,

      // ===== WISTIA =====
      /wistia\.com/,
      /wistia\.net/,
      /fast\.wistia/,

      // ===== INTERCOM =====
      /intercom\.io/,
      /intercomcdn\.com/,
      /js\.intercomassets\.com/,
    ],
    beforeSend(event, hint) {
      // Drop events with no useful information (empty errors)
      if (!event.exception?.values?.[0]?.value && !event.message) {
        return null
      }

      const frames = event.exception?.values?.[0]?.stacktrace?.frames || []

      // Filter browser extension errors by stack trace
      const isBrowserExtension = frames.some((frame) =>
        frame.filename?.match(/^(chrome|moz|safari)(-web)?-extension:\/\//)
      )
      if (isBrowserExtension) {
        return null
      }

      // Filter out any errors originating from third-party scripts
      const isThirdPartyError = frames.some(
        (frame) =>
          frame.filename?.includes('otSDKStub') ||
          frame.filename?.includes('onetrust') ||
          frame.filename?.includes('cookielaw') ||
          frame.filename?.includes('newrelic') ||
          frame.filename?.includes('browser-agent') ||
          frame.filename?.includes('nr-data') ||
          frame.filename?.includes('chmln') ||
          frame.filename?.includes('chameleon') ||
          frame.filename?.includes('messo') ||
          frame.filename?.includes('wistia') ||
          frame.filename?.includes('intercom')
      )
      if (isThirdPartyError) {
        return null
      }

      return event
    },
  })
}

export function setSentryUser(user, context = {}) {
  if (!user) {
    Sentry.setUser(null)
    return
  }
  
  const userData = {
    id: user.id || user,
    email: user.email,
    username: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}`.trim() : undefined,
  }

  // Add masquerading context if provided
  if (context.masquerading) {
    userData.masquerading = true
    userData.original_user_id = context.originalUser
  }
  
  Sentry.setUser(userData)
}

export function captureException(error, context) {
  Sentry.captureException(error, {
    contexts: {
      custom: context,
    },
  })
}

export const withSentryRouting = Sentry.withSentryRouting